import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import feather from 'feather-icons';
import { StockService } from '../../../../../services/stock/stock.service';
import { ProductsService } from '../../../../../services/products/products.service';
import { WarehouseService } from '../../../../../services/warehouse/warehouse.service';
import { StationService } from '../../../../../services/station.service';
import { getStationIdFromLocalStorage } from '../../../../../services/parametre-document/parametre-document-state.service';
import { DocumentService } from '../../../../../services/document/document.service';
import { ToastrService } from 'ngx-toastr';

export interface MouvementStockRowView {
    date: string;
    document: string;
    documentSub: string;
    /** Libellé colonne ÉTAT : attribut `statut` du mouvement, puis statuts document / type de mouvement. */
    etat: string;
    etatBadgeClass: string;
    contact: string;
    /** document_id du mouvement → GET /api/documents/:id pour nom_client */
    documentId: number | null;
    utilisateur: string;
    /** Si présent, résolution prénom + nom via GET /api/settings/station/:station_id */
    utilisateurUserId: number | null;
    /** Tous les identifiants numériques possibles sur la ligne mouvement (user_id, coordonnees.id, etc.) */
    utilisateurLookupIds: number[];
    /** E-mail utilisateur sur le mouvement (correspondance coordonnees.email) */
    utilisateurEmailHint: string | null;
    quantiteEntrante: string;
    quantiteEntranteHasValue: boolean;
    quantiteSortante: string;
    quantiteSortanteIsEmpty: boolean;
    enStock: string;
    entrepot: string;
    /** Si l’API le fournit, filtre précis par entrepôt */
    entrepotId: number | null;
    /**
     * Segment de base vers la page approve-vente (ex. `/vente/devis`) ;
     * null si le type n’a pas été déduit depuis le mouvement (repli : GET document).
     */
    approveRouteBase: string | null;
}

@Component({
    selector: 'app-mouvement-stock-produit',
    templateUrl: './mouvement-stock-produit.component.html',
    styleUrls: ['./mouvement-stock-produit.component.scss']
})
export class MouvementStockProduitComponent implements OnInit, AfterViewInit {
    showFilter = false;
    filterSummary: string = '';
    searchTerm = '';

    productErpId!: number;
    loading = false;
    errorMessage = '';
    stockMeta: any = null;
    mouvements: MouvementStockRowView[] = [];
    /** Réponse normalisée getDetailsProduct (ERP) pour informations_logistique */
    productDetail: any = null;
    /**
     * Stock agrégé GET /api/stock/product/:product_id (ERP id) — total tous entrepôts + détail pour infobulle.
     */
    stockAllEntrepots: {
        total: number;
        detailLines: string[];
        byEntrepotId: Map<number, number>;
    } | null = null;

    /**
     * Valeur fictive ng-select pour « tous les entrepôts » (somme min/max, pas de filtre sur le tableau).
     * Ne peut pas entrer en conflit avec un id API réel (positifs).
     */
    readonly ALL_ENTREPOTS_SELECT_ID = -1;
    /** Liste des entrepôts de la station (select Emplacement) ; première entrée = « Tous les entrepôts ». */
    entrepotsList: { id: number; nom: string }[] = [
        { id: this.ALL_ENTREPOTS_SELECT_ID, nom: 'Tous les entrepôts' }
    ];
    /** Par défaut : tous les entrepôts. null après clear = même comportement que tous. */
    selectedEntrepotId: number | null = this.ALL_ENTREPOTS_SELECT_ID;
    isLoadingEntrepots = false;

    /** Pagination (liste filtrée en mémoire). */
    currentPage = 1;
    pageSize = 10;
    readonly pageSizeOptions = [10, 25, 50, 100];

    /** id (user ou coordonnees.id) → « Prénom Nom » */
    private userDisplayByUserId = new Map<number, string>();
    /** email (minuscules) → « Prénom Nom » (coordonnees[].email) */
    private userDisplayByEmail = new Map<string, string>();
    /** Libellés « Prénom Nom » pour correspondance texte (mouvement sans id fiable) */
    private coordonneesDisplayLabels: string[] = [];
    /** document_id → nom_client (GET /api/documents/:id) */
    private contactDisplayByDocumentId = new Map<number, string>();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private stockService: StockService,
        private productsService: ProductsService,
        private warehouseService: WarehouseService,
        private stationService: StationService,
        private documentService: DocumentService,
        private toastr: ToastrService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        const resolved = this.resolveErpProductIdFromRoute();
        if (resolved == null) {
            this.errorMessage = 'Identifiant produit invalide.';
            return;
        }
        this.productErpId = resolved;
        this.loadEntrepots();
        this.loadProductLogisticInfo();
        this.loadStockAllEntrepots();
        this.loadStationUserDisplayMap();
        this.loadData();
    }

    /**
     * GET /api/settings/station/:station_id — construit la table id utilisateur → « prénom nom »
     * (coordonnees[], users, utilisateurs, etc.).
     */
    private loadStationUserDisplayMap(): void {
        const stationId =
            getStationIdFromLocalStorage() || this.getUserAndStationIds().stationId;
        if (!stationId) {
            return;
        }
        this.stationService.getStation(stationId).subscribe({
            next: (res: any) => {
                const station = this.unwrapStationResponse(res);
                if (!station || typeof station !== 'object') {
                    return;
                }
                this.applyStationPayloadToUserMap(station);
                this.cdr.detectChanges();
            },
            error: () => {}
        });
    }

    /** Réponses GET /api/settings/station/:id variables (success + station, ou data seule). */
    private unwrapStationResponse(res: any): any | null {
        if (!res || typeof res !== 'object') {
            return null;
        }
        if (res.station && typeof res.station === 'object') {
            return res.station;
        }
        const d = res.data;
        if (d?.station && typeof d.station === 'object') {
            return d.station;
        }
        if (d && typeof d === 'object' && !Array.isArray(d) && (d.coordonnees != null || d.id != null)) {
            return d;
        }
        return null;
    }

    private applyStationPayloadToUserMap(station: any): void {
        this.userDisplayByUserId.clear();
        this.userDisplayByEmail.clear();
        this.coordonneesDisplayLabels = [];

        const put = (rawId: unknown, prenom: string, nom: string) => {
            if (rawId === undefined || rawId === null || rawId === '') {
                return;
            }
            const id = Number(rawId);
            if (!Number.isFinite(id) || id <= 0) {
                return;
            }
            const p = (prenom || '').trim();
            const n = (nom || '').trim();
            const label = `${p} ${n}`.trim();
            if (!label) {
                return;
            }
            this.userDisplayByUserId.set(id, label);
        };

        const registerCoord = (c: any) => {
            const prenom = String(c.prenom ?? '').trim();
            const nom = String(c.nom ?? '').trim();
            const label = `${prenom} ${nom}`.trim();
            if (!label) {
                return;
            }
            this.coordonneesDisplayLabels.push(label);
            /** id ligne coordonnees_station */
            if (c.id != null && c.id !== '' && !isNaN(Number(c.id))) {
                this.userDisplayByUserId.set(Number(c.id), label);
            }
            /** Si l’API lie l’utilisateur applicatif (souvent différent de coordonnees.id) */
            if (c.user_id != null && c.user_id !== '' && !isNaN(Number(c.user_id))) {
                this.userDisplayByUserId.set(Number(c.user_id), label);
            }
            if (c.utilisateur_id != null && c.utilisateur_id !== '' && !isNaN(Number(c.utilisateur_id))) {
                this.userDisplayByUserId.set(Number(c.utilisateur_id), label);
            }
            const mail = String(c.email ?? '').trim().toLowerCase();
            if (mail) {
                this.userDisplayByEmail.set(mail, label);
            }
        };

        const coords = station.coordonnees;
        const coordList: any[] = Array.isArray(coords)
            ? coords
            : coords && typeof coords === 'object'
              ? [coords]
              : [];
        for (const c of coordList) {
            if (!c || typeof c !== 'object') {
                continue;
            }
            const prenom = String(c.prenom ?? '').trim();
            const nom = String(c.nom ?? '').trim();
            registerCoord(c);
            const nested = c.utilisateur ?? c.user;
            if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
                put(
                    nested.id ?? nested.user_id,
                    String(nested.prenom ?? nested.first_name ?? prenom ?? ''),
                    String(nested.nom ?? nested.last_name ?? nom ?? '')
                );
            }
            put(c.user_id ?? c.utilisateur_id, prenom, nom);
        }

        const extraLists = [station.users, station.utilisateurs, station.collaborateurs].filter(
            (x: unknown) => Array.isArray(x)
        ) as any[][];
        for (const list of extraLists) {
            for (const u of list) {
                if (!u || typeof u !== 'object') {
                    continue;
                }
                put(
                    u.id ?? u.user_id,
                    String(u.prenom ?? u.first_name ?? u.firstName ?? ''),
                    String(u.nom ?? u.last_name ?? u.lastName ?? u.name ?? '')
                );
            }
        }
    }

    /** Libellé colonne UTILISATEUR : prénom + nom depuis coordonnees (id, e-mail, texte), sinon texte mouvement. */
    getUtilisateurDisplay(row: MouvementStockRowView): string {
        const ids = row.utilisateurLookupIds ?? [];
        for (const id of ids) {
            const fromStation = this.userDisplayByUserId.get(id);
            if (fromStation) {
                return fromStation;
            }
        }
        if (row.utilisateurUserId != null) {
            const one = this.userDisplayByUserId.get(row.utilisateurUserId);
            if (one) {
                return one;
            }
        }
        const hint = (row.utilisateurEmailHint || '').trim().toLowerCase();
        if (hint) {
            const byMail = this.userDisplayByEmail.get(hint);
            if (byMail) {
                return byMail;
            }
        }
        const raw = (row.utilisateur || '').trim();
        if (raw.includes('@')) {
            const byMail = this.userDisplayByEmail.get(raw.toLowerCase());
            if (byMail) {
                return byMail;
            }
        }
        const textMatch = this.matchUtilisateurTextToCoordonnees(raw);
        if (textMatch) {
            return textMatch;
        }
        /** Une seule fiche coordonnées : repli d’affichage si la ligne n’a pas de nom exploitable */
        if (this.coordonneesDisplayLabels.length === 1) {
            const only = this.coordonneesDisplayLabels[0];
            const looksLikeMissingName =
                raw === '' ||
                raw === '—' ||
                raw === '-' ||
                /^[\d\s]+$/.test(raw) ||
                raw.toLowerCase() === 'utilisateur' ||
                raw.toLowerCase() === 'user';
            if (only && looksLikeMissingName) {
                return only;
            }
        }
        return row.utilisateur;
    }

    private normalizePersonLabel(s: string): string {
        return (s || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    }

    /** Si le mouvement renvoie déjà « Prénom Nom » proche d’une coordonnée */
    private matchUtilisateurTextToCoordonnees(utilText: string): string | null {
        const t = this.normalizePersonLabel(utilText);
        if (!t || t === '—' || t === '-') {
            return null;
        }
        for (const lab of this.coordonneesDisplayLabels) {
            const ln = this.normalizePersonLabel(lab);
            if (!ln) {
                continue;
            }
            if (ln === t || t.includes(ln) || ln.includes(t)) {
                return lab;
            }
        }
        return null;
    }

    /**
     * Pour chaque document_id distinct sur les lignes mouvement : GET /api/documents/:id
     * → nom_client / nom_prenom / nom_entreprise (clients, fournisseurs, etc.).
     */
    private loadContactLabelsFromDocuments(rawRows: any[]): void {
        const ids = new Set<number>();
        for (const m of rawRows) {
            const id =
                m?.document_id ??
                m?.document?.id ??
                m?.doc_id ??
                m?.id_document;
            if (id != null && id !== '' && !isNaN(Number(id))) {
                ids.add(Number(id));
            }
        }
        const need = [...ids].filter((id) => !this.contactDisplayByDocumentId.has(id));
        if (need.length === 0) {
            return;
        }
        forkJoin(
            need.map((documentId) =>
                this.documentService.getDocumentById(documentId).pipe(
                    map((res) => ({
                        id: documentId,
                        label: this.extractContactPartyLabelFromDocumentApi(res)
                    })),
                    catchError(() => of({ id: documentId, label: null as string | null }))
                )
            )
        ).subscribe((results) => {
            for (const { id, label } of results) {
                if (label) {
                    this.contactDisplayByDocumentId.set(id, label);
                }
            }
            this.cdr.detectChanges();
        });
    }

    /**
     * Client ou fournisseur : priorité nom_client, nom_prenom, puis nom_entreprise, puis nom.
     */
    private pickContactPartyLabel(party: any): string | null {
        if (!party || typeof party !== 'object') {
            return null;
        }
        const name = String(
            party.nom_client ??
                party.nom_prenom ??
                party.nom_entreprise ??
                party.nom ??
                ''
        ).trim();
        return name || null;
    }

    private extractContactPartyLabelFromDocumentApi(res: any): string | null {
        if (!res || typeof res !== 'object') {
            return null;
        }
        const root = res.data !== undefined && res.data !== null ? res.data : res;
        const doc =
            root &&
            typeof root === 'object' &&
            !Array.isArray(root) &&
            (root as any).document &&
            typeof (root as any).document === 'object'
                ? (root as any).document
                : root;
        if (!doc || typeof doc !== 'object' || Array.isArray(doc)) {
            return null;
        }
        const d = doc as any;

        const clients = d.clients;
        if (Array.isArray(clients) && clients.length > 0) {
            const fromClient = this.pickContactPartyLabel(clients[0]);
            if (fromClient) {
                return fromClient;
            }
        }
        const cd = d.client_data ?? d.client;
        if (cd && typeof cd === 'object') {
            const fromCd = this.pickContactPartyLabel(cd);
            if (fromCd) {
                return fromCd;
            }
        }

        const fourn =
            (Array.isArray(d.fournisseurs) && d.fournisseurs[0]) ||
            (Array.isArray(d.fornisseurs) && d.fornisseurs[0]) ||
            d.fournisseur_data ||
            d.fournisseur;
        if (fourn && typeof fourn === 'object') {
            const fromF = this.pickContactPartyLabel(fourn);
            if (fromF) {
                return fromF;
            }
        }

        return null;
    }

    /** Colonne CONTACT : nom issu du document (GET by id), sinon champs mouvement. */
    getContactDisplay(row: MouvementStockRowView): string {
        if (row.documentId != null) {
            const fromDoc = this.contactDisplayByDocumentId.get(row.documentId);
            if (fromDoc) {
                return fromDoc;
            }
        }
        return row.contact;
    }

    /** GET /api/entrepots — tous les entrepôts de la station. */
    private loadEntrepots(): void {
        const { userId, stationId } = this.getUserAndStationIds();
        if (!userId || !stationId) {
            this.entrepotsList = [{ id: this.ALL_ENTREPOTS_SELECT_ID, nom: 'Tous les entrepôts' }];
            return;
        }
        this.isLoadingEntrepots = true;
        this.warehouseService.getAllWarehouses(userId, stationId).subscribe({
            next: (raw: any[]) => {
                const arr = Array.isArray(raw) ? raw : [];
                const warehouses = arr
                    .map((e: any) => ({
                        id: Number(e?.id ?? e?.entrepot_id),
                        nom: String(e?.nom ?? e?.name ?? e?.label ?? '').trim() || `Entrepôt #${e?.id ?? ''}`
                    }))
                    .filter((e) => !isNaN(e.id) && e.id > 0);
                this.entrepotsList = [
                    { id: this.ALL_ENTREPOTS_SELECT_ID, nom: 'Tous les entrepôts' },
                    ...warehouses
                ];
                this.isLoadingEntrepots = false;
                setTimeout(() => feather.replace());
            },
            error: () => {
                this.entrepotsList = [{ id: this.ALL_ENTREPOTS_SELECT_ID, nom: 'Tous les entrepôts' }];
                this.isLoadingEntrepots = false;
            }
        });
    }

    private getUserAndStationIds(): { userId: number; stationId: number } {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const userId = Number(currentUser?.id) || 0;
        const station = currentUser?.stations?.[0];
        const stationId =
            Number(station?.station_id ?? station?.id ?? currentUser?.station_id) || 0;
        return { userId, stationId };
    }

    onEntrepotFilterChange(): void {
        this.resetPagination();
        setTimeout(() => feather.replace());
    }

    /** GET /api/stock/product/:product_id — quantité totale et répartition par entrepôt */
    private loadStockAllEntrepots(): void {
        this.stockService.getStockDetailsByProduct(this.productErpId).subscribe({
            next: (data: any) => {
                this.applyStockProductApiData(data);
                setTimeout(() => feather.replace());
            },
            error: () => {
                this.stockAllEntrepots = null;
            }
        });
    }

    private applyStockProductApiData(data: any): void {
        if (!data || typeof data !== 'object') {
            this.stockAllEntrepots = null;
            return;
        }
        const by =
            data.stocks_by_entrepot ??
            data.stocksByEntrepot ??
            data.stocks_by_warehouse ??
            [];
        let total = Number(data.total_quantity ?? data.totalQuantity);
        const detailLines: string[] = [];
        const byEntrepotId = new Map<number, number>();
        if (Array.isArray(by) && by.length > 0) {
            const sumBy = by.reduce(
                (s: number, row: any) => s + Number(row?.quantity ?? row?.qty ?? 0),
                0
            );
            total = sumBy;
            for (const row of by) {
                const qty = Number(row?.quantity ?? row?.qty ?? 0);
                const eid = row?.entrepot_id ?? row?.entrepot?.id ?? row?.warehouse_id;
                if (eid != null && !isNaN(Number(eid))) {
                    byEntrepotId.set(Number(eid), qty);
                }
                const nom =
                    row?.entrepot_nom ??
                    row?.entrepot?.nom ??
                    row?.nom_entrepot ??
                    row?.warehouse?.name ??
                    (row?.entrepot_id != null ? `Entrepôt #${row.entrepot_id}` : '');
                const label = nom ? `${nom}: ${qty}` : String(qty);
                detailLines.push(label);
            }
        } else if (!Number.isFinite(total)) {
            total = 0;
        }
        this.stockAllEntrepots = {
            total: Number(total.toFixed(3)),
            detailLines,
            byEntrepotId
        };
    }

    /**
     * Emplacement / min / max depuis GET produit : informations_logistique (aligné sur logistic-info).
     */
    private loadProductLogisticInfo(): void {
        this.productsService.getDetailsProduct(this.productErpId).subscribe({
            next: (res: any) => {
                const p = res?.data ?? res;
                this.productDetail = p;
                setTimeout(() => feather.replace());
            },
            error: () => {
                this.productDetail = null;
            }
        });
    }

    /**
     * Remonte la chaîne des routes (ex. outlet imbriqué) jusqu'au param :id du détail produit.
     */
    private resolveErpProductIdFromRoute(): number | null {
        let r: ActivatedRoute | null = this.route;
        while (r) {
            const id = r.snapshot.paramMap.get('id');
            if (id != null && id !== '') {
                const n = +id;
                if (!isNaN(n)) return n;
            }
            r = r.parent;
        }
        return null;
    }

    ngAfterViewInit(): void {
        feather.replace();
    }

    get filteredMouvements(): MouvementStockRowView[] {
        let list = this.mouvements;
        const warehouseFilterId = this.getSelectedWarehouseIdForFilter();
        if (warehouseFilterId != null) {
            const selNom = this.entrepotsList.find((e) => e.id === warehouseFilterId)?.nom ?? '';
            list = list.filter((m) =>
                this.movementMatchesEntrepot(m, warehouseFilterId, selNom)
            );
        }
        const t = this.searchTerm.trim().toLowerCase();
        if (!t) return list;
        return list.filter((m) => {
            const utilLabel = this.getUtilisateurDisplay(m).toLowerCase();
            const contactLabel = this.getContactDisplay(m).toLowerCase();
            return (
                m.date.toLowerCase().includes(t) ||
                m.document.toLowerCase().includes(t) ||
                m.etat.toLowerCase().includes(t) ||
                m.contact.toLowerCase().includes(t) ||
                contactLabel.includes(t) ||
                utilLabel.includes(t) ||
                m.utilisateur.toLowerCase().includes(t) ||
                m.quantiteEntrante.toLowerCase().includes(t) ||
                m.quantiteSortante.toLowerCase().includes(t) ||
                m.entrepot.toLowerCase().includes(t)
            );
        });
    }

    /** Tranche affichée pour la page courante (page bornée au total de pages). */
    get pagedMouvements(): MouvementStockRowView[] {
        const all = this.filteredMouvements;
        const page = this.clampedPage;
        const start = (page - 1) * this.pageSize;
        return all.slice(start, start + this.pageSize);
    }

    /**
     * Page affichée (1…totalPages) : si `currentPage` dépasse le total après filtre, on borne sans casser le slice.
     */
    get clampedPage(): number {
        const tp = this.totalPages;
        if (this.currentPage < 1) {
            return 1;
        }
        if (this.currentPage > tp) {
            return tp;
        }
        return this.currentPage;
    }

    get totalPages(): number {
        const n = this.filteredMouvements.length;
        if (n <= 0) {
            return 1;
        }
        return Math.ceil(n / this.pageSize);
    }

    get paginationFrom(): number {
        const n = this.filteredMouvements.length;
        if (n === 0) {
            return 0;
        }
        const page = this.clampedPage;
        return (page - 1) * this.pageSize + 1;
    }

    get paginationTo(): number {
        const n = this.filteredMouvements.length;
        if (n === 0) {
            return 0;
        }
        return Math.min(this.clampedPage * this.pageSize, n);
    }

    goToPage(page: number): void {
        const max = this.totalPages;
        this.currentPage = Math.min(Math.max(1, page), max);
        setTimeout(() => feather.replace());
    }

    onPageSizeChange(): void {
        this.currentPage = 1;
        setTimeout(() => feather.replace());
    }

    onPaginationPageSizeChange(size: number): void {
        this.pageSize = size;
        this.onPageSizeChange();
    }

    private resetPagination(): void {
        this.currentPage = 1;
    }

    private movementMatchesEntrepot(m: MouvementStockRowView, entrepotId: number, selNom: string): boolean {
        if (m.entrepotId != null && m.entrepotId === entrepotId) {
            return true;
        }
        if (selNom && this.normalizeWarehouseLabel(m.entrepot) === this.normalizeWarehouseLabel(selNom)) {
            return true;
        }
        return (
            selNom !== '' &&
            m.entrepot.toLowerCase().includes(selNom.toLowerCase().trim())
        );
    }

    private normalizeWarehouseLabel(s: string): string {
        return (s || '').toLowerCase().trim().replace(/\s+/g, ' ');
    }

    loadData(): void {
        this.loading = true;
        this.errorMessage = '';
        this.stockService
            .getStockContextForMouvements(this.productErpId)
            .pipe(
                switchMap(({ stockId, meta }) => {
                    this.stockMeta = meta;
                    if (stockId == null) {
                        this.errorMessage =
                            "Ce produit n'est pas encore référencé dans le module stock.";
                        return of([]);
                    }
                    return this.stockService.getMouvementsStockByStockProductId(stockId);
                }),
                finalize(() => {
                    this.loading = false;
                    setTimeout(() => feather.replace());
                })
            )
            .subscribe({
                next: (rows: any[]) => {
                    const list = rows || [];
                    this.mouvements = list.map((r) => this.normalizeRow(r));
                    this.resetPagination();
                    this.loadContactLabelsFromDocuments(list);
                    /** Si le 1er chargement station était trop tôt (station_id = 0), réessayer après les mouvements. */
                    queueMicrotask(() => {
                        if (this.userDisplayByUserId.size === 0 && this.coordonneesDisplayLabels.length === 0) {
                            this.loadStationUserDisplayMap();
                        }
                    });
                },
                error: () => {
                    this.errorMessage = 'Impossible de charger les mouvements de stock.';
                    this.toastr.error(this.errorMessage);
                }
            });
    }

    private normalizeRow(m: any): MouvementStockRowView {
        const dateRaw =
            m?.date ?? m?.created_at ?? m?.date_mouvement ?? m?.updated_at ?? '';
        const dateStr = this.formatDate(dateRaw);
        const doc = String(m?.reference_document ?? m?.document ?? m?.reference ?? '—');
        const docSub = String(m?.type_document ?? m?.document_type ?? '');
        const statutDocument = this.extractDocumentStatutLabel(m);
        const etat =
            statutDocument ??
            String(m?.type ?? m?.etat ?? m?.statut ?? '—');

        let documentId: number | null = null;
        const docIdRaw =
            m?.document_id ??
            m?.document?.id ??
            m?.doc_id ??
            m?.id_document;
        if (docIdRaw != null && docIdRaw !== '' && !isNaN(Number(docIdRaw))) {
            documentId = Number(docIdRaw);
        }

        const contact = String(m?.contact ?? m?.contact_nom ?? m?.client ?? m?.fournisseur ?? '—');

        const utilisateurBrut = m?.utilisateur;
        const userObj =
            m?.user ??
            (typeof utilisateurBrut === 'object' && utilisateurBrut !== null ? utilisateurBrut : null);
        let utilisateurUserId: number | null = null;
        const lookupIds = new Set<number>();
        const addLookupId = (v: unknown) => {
            if (v === undefined || v === null || v === '') {
                return;
            }
            const n = Number(v);
            if (Number.isFinite(n) && n > 0) {
                lookupIds.add(Math.floor(n));
            }
        };
        if (userObj && typeof userObj === 'object' && !Array.isArray(userObj)) {
            const uid = (userObj as any).id ?? (userObj as any).user_id;
            if (uid != null && uid !== '' && !isNaN(Number(uid))) {
                utilisateurUserId = Number(uid);
            }
            addLookupId((userObj as any).id);
            addLookupId((userObj as any).user_id);
            addLookupId((userObj as any).coordonnees_station_id);
        }
        if (utilisateurUserId == null) {
            const raw =
                m?.user_id ??
                m?.utilisateur_id ??
                m?.created_by_id ??
                m?.coordonnees_station_id ??
                m?.coordonnee_id ??
                m?.coordonnees?.id ??
                m?.id_user ??
                m?.created_user_id ??
                (typeof m?.created_by === 'number' ? m.created_by : null);
            if (raw != null && raw !== '' && !isNaN(Number(raw))) {
                utilisateurUserId = Number(raw);
            }
        }
        if (typeof m?.created_by === 'string' && m.created_by.trim() !== '' && !isNaN(Number(m.created_by))) {
            addLookupId(m.created_by);
        }
        for (const key of [
            'user_id',
            'utilisateur_id',
            'created_by_id',
            'coordonnees_station_id',
            'coordonnee_id',
            'created_by',
            'id_user',
            'created_user_id'
        ] as const) {
            addLookupId(m?.[key]);
        }
        addLookupId(m?.coordonnees?.id);
        if (utilisateurUserId != null) {
            addLookupId(utilisateurUserId);
        }

        let utilisateurEmailHint: string | null = null;
        const mailRaw =
            (userObj as any)?.email ??
            m?.user?.email ??
            (typeof utilisateurBrut === 'string' && utilisateurBrut.includes('@') ? utilisateurBrut : null);
        if (mailRaw != null && String(mailRaw).trim() !== '') {
            utilisateurEmailHint = String(mailRaw).trim().toLowerCase();
        }

        let user = '—';
        if (userObj && typeof userObj === 'object' && !Array.isArray(userObj)) {
            const uo = userObj as any;
            const pp = String(uo.prenom ?? uo.first_name ?? '').trim();
            const nn = String(uo.nom ?? uo.last_name ?? '').trim();
            const fromNames = `${pp} ${nn}`.trim();
            user = fromNames || String(uo.name ?? uo.email ?? '—');
        } else {
            user = String(
                (typeof utilisateurBrut === 'string' ? utilisateurBrut : '') ||
                    m?.user_name ||
                    m?.user?.name ||
                    (typeof m?.created_by === 'string' ? m.created_by : '') ||
                    '—'
            );
            if (user === '' || user === '—') {
                const fallback = m?.user_name ?? m?.user?.name ?? m?.created_by;
                if (fallback != null && fallback !== '' && typeof fallback !== 'object') {
                    user = String(fallback);
                }
            }
        }

        const movementTypeStr = String(m?.type ?? m?.etat ?? '').toLowerCase();
        let qIn =
            m?.quantite_entrante ??
            m?.quantite_entree ??
            m?.quantite_in ??
            m?.incoming_quantity ??
            m?.qty_in;
        let qOut =
            m?.quantite_sortante ??
            m?.quantite_out ??
            m?.quantity_out ??
            m?.quantite_abs;

        const qGeneric = m?.quantite ?? m?.quantity;
        if (
            (qIn === undefined || qIn === null || qIn === '') &&
            qGeneric != null &&
            qGeneric !== '' &&
            (movementTypeStr.includes('entr') ||
                movementTypeStr.includes('reçu') ||
                movementTypeStr.includes('recu') ||
                movementTypeStr.includes('in'))
        ) {
            qIn = qGeneric;
        }
        if (
            (qOut === undefined || qOut === null || qOut === '') &&
            qGeneric != null &&
            qGeneric !== '' &&
            (movementTypeStr.includes('sort') || movementTypeStr.includes('out'))
        ) {
            qOut = qGeneric;
        }

        let numIn = this.parseQty(qIn);
        let numOut = this.parseQty(qOut);

        const stockAvant = this.parseQty(
            m?.stock_avant ?? m?.stock_before ?? m?.stock_avant_entrepot
        );
        const stockApres = this.parseQty(
            m?.stock_apres ?? m?.stock_après ?? m?.stock_after ?? m?.stock_apres_entrepot
        );

        /**
         * Si les quantités explicites sont absentes : déduire du mouvement de stock.
         * stock_avant < stock_apres → entrée = différence ; sinon sortie = différence (si avant > après).
         */
        if (stockAvant != null && stockApres != null) {
            if (stockApres > stockAvant) {
                const deltaIn = stockApres - stockAvant;
                if (deltaIn > 0 && (numIn == null || numIn === 0)) {
                    numIn = deltaIn;
                }
            } else if (stockAvant > stockApres) {
                const deltaOut = stockAvant - stockApres;
                if (deltaOut > 0 && (numOut == null || numOut === 0)) {
                    numOut = deltaOut;
                }
            }
        }

        const entInStr =
            numIn != null && numIn > 0 ? String(numIn) : qIn !== undefined && qIn !== null && qIn !== '' ? String(qIn) : '';
        const entInHas = entInStr !== '' && entInStr !== '0';

        const sortanteStr =
            numOut != null && numOut > 0
                ? String(numOut)
                : qOut !== undefined && qOut !== null && qOut !== ''
                  ? String(qOut)
                  : '';
        const sortanteEmpty =
            sortanteStr === '' || sortanteStr === '0' || sortanteStr === '—';

        const enStock = String(
            m?.stock_apres ?? m?.stock_après ?? m?.en_stock ?? m?.stock_apres_entrepot ?? '—'
        );
        const entrepot = String(
            m?.entrepot?.nom ??
                m?.entrepot_nom ??
                m?.warehouse?.name ??
                m?.entrepot_libelle ??
                (typeof m?.entrepot === 'string' ? m.entrepot : '') ??
                '—'
        );
        const entrepotIdRaw =
            m?.entrepot_id ?? m?.entrepot?.id ?? m?.warehouse_id ?? m?.warehouse?.id ?? null;
        const entrepotId =
            entrepotIdRaw != null && entrepotIdRaw !== '' && !isNaN(Number(entrepotIdRaw))
                ? Number(entrepotIdRaw)
                : null;
        return {
            date: dateStr,
            document: doc,
            documentSub: docSub,
            etat,
            etatBadgeClass: this.badgeClassEtatColumn(etat),
            contact,
            documentId,
            utilisateur: user,
            utilisateurUserId,
            utilisateurLookupIds: Array.from(lookupIds),
            utilisateurEmailHint,
            quantiteEntrante: entInHas ? entInStr : '—',
            quantiteEntranteHasValue: entInHas,
            quantiteSortante: sortanteEmpty ? '--' : sortanteStr,
            quantiteSortanteIsEmpty: sortanteEmpty,
            enStock,
            entrepot,
            entrepotId,
            approveRouteBase: this.resolveApproveVenteRouteBase(m)
        };
    }

    /**
     * Déduit le chemin de base (ex. `/vente/devis`) depuis les champs du mouvement pour ouvrir approve-vente.
     */
    private resolveApproveVenteRouteBase(m: any): string | null {
        let label = '';
        const td = m?.type_document ?? m?.typeDocument;
        if (td && typeof td === 'object' && !Array.isArray(td)) {
            label = String(td.titre ?? td.libelle ?? td.nom ?? td.label ?? '').trim();
        }
        if (!label) {
            label = String(
                m?.type_document_label ??
                    m?.document_type_label ??
                    m?.type_document ??
                    m?.document_type ??
                    ''
            ).trim();
        }
        if (!label && m?.document && typeof m.document === 'object') {
            const dtd = m.document.typeDocument ?? m.document.type_document;
            if (dtd && typeof dtd === 'object') {
                label = String(dtd.titre ?? dtd.libelle ?? '').trim();
            }
        }
        return this.mapDocumentTypeLabelToApproveBasePath(label);
    }

    /**
     * Aligné sur approve-vente `getBasePath()` : libellé type document → préfixe de route.
     */
    private mapDocumentTypeLabelToApproveBasePath(label: string): string | null {
        if (!label || !String(label).trim()) {
            return null;
        }
        const n = String(label)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toUpperCase()
            .replace(/\s+/g, ' ')
            .trim();

        if (n.includes('AVOIR') && n.includes('FOURN')) {
            return '/achat/avoir-fournisseur';
        }
        if (n.includes('FACTURE') && n.includes('FOURN')) {
            return '/achat/facture-fournisseur';
        }
        if (n.includes('COMMANDE') && n.includes('FOURN')) {
            return '/achat/commande-fournisseur';
        }
        if (n.includes('RECEPTION') || (n.includes('BON') && n.includes('RECEPTION'))) {
            return '/achat/bon-reception';
        }
        if (n.includes('TRANSFERT')) {
            return '/bon-transfert';
        }
        if (n.includes('INVENTAIRE')) {
            return '/inventaire';
        }
        if (n.includes('LIVRAISON')) {
            return '/vente/bon-livraison';
        }
        if (n.includes('SORTIE')) {
            return '/vente/bon-sortie';
        }
        if (n.includes('COMMANDE') && n.includes('CLIENT')) {
            return '/vente/commande-client';
        }
        if (n.includes('DEVIS')) {
            return '/vente/devis';
        }
        if (n.includes('FACTURE')) {
            return '/vente/facture';
        }
        if (n.includes('AVOIR')) {
            return '/vente/avoir';
        }
        return null;
    }

    private extractApproveRouteBaseFromDocumentApi(res: any): string | null {
        if (!res || typeof res !== 'object') {
            return null;
        }
        const root = res.data !== undefined && res.data !== null ? res.data : res;
        const doc =
            root &&
            typeof root === 'object' &&
            (root as any).document &&
            typeof (root as any).document === 'object'
                ? (root as any).document
                : root;
        if (!doc || typeof doc !== 'object' || Array.isArray(doc)) {
            return null;
        }
        const titre = String(
            (doc as any).typeDocument?.titre ??
                (doc as any).type_document?.titre ??
                (doc as any).documentType ??
                ''
        ).trim();
        return this.mapDocumentTypeLabelToApproveBasePath(titre);
    }

    /** Ouvre la page approve-vente (validation) pour le document lié au mouvement. */
    openDocumentInApproveVente(row: MouvementStockRowView, event?: Event): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        if (row.documentId == null || row.documentId <= 0) {
            this.toastr.warning('Aucun document lié à ce mouvement.');
            return;
        }
        const id = row.documentId;
        const navigate = (base: string) => {
            this.router.navigate([base, 'action', 'validation'], { queryParams: { id } });
        };
        if (row.approveRouteBase) {
            navigate(row.approveRouteBase);
            return;
        }
        this.documentService
            .getDocumentById(id)
            .pipe(
                take(1),
                map((res) => this.extractApproveRouteBaseFromDocumentApi(res)),
                catchError(() => of(null))
            )
            .subscribe((base) => {
                if (base) {
                    navigate(base);
                } else {
                    this.toastr.warning(
                        'Impossible d’ouvrir le document : type non reconnu. Utilisez la liste des documents.'
                    );
                }
            });
    }

    private parseQty(v: any): number | null {
        if (v === undefined || v === null || v === '') return null;
        const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
        return isNaN(n) ? null : n;
    }

    /** Date type capture : « 10 / 04 / 2025 » */
    private formatDate(value: any): string {
        if (value == null || value === '') return '—';
        try {
            const d = new Date(value);
            if (isNaN(d.getTime())) return String(value);
            const pad = (n: number) => (n < 10 ? '0' + n : String(n));
            return `${pad(d.getDate())} / ${pad(d.getMonth() + 1)} / ${d.getFullYear()}`;
        } catch {
            return String(value);
        }
    }

    /**
     * Colonne ÉTAT : d’abord `statut` sur la ligne mouvement_stock, puis champs document / repli.
     */
    private extractDocumentStatutLabel(m: any): string | null {
        const st = m?.statut;
        if (st !== undefined && st !== null && st !== '') {
            if (typeof st === 'object' && !Array.isArray(st)) {
                const fromObj = String(st.titre ?? st.nom ?? st.libelle ?? st.label ?? '').trim();
                if (fromObj) {
                    return fromObj;
                }
            } else {
                const fromScalar = String(st).trim();
                if (fromScalar) {
                    return fromScalar;
                }
            }
        }

        const candidates: any[] = [
            m?.statut_document,
            m?.statutDocument,
            m?.document_statut,
            m?.document_status,
            m?.statut_du_document,
            m?.document_statut_libelle,
            m?.statut_document_libelle,
            m?.statut_document_titre,
            m?.document?.statut?.titre,
            m?.document?.statut?.nom,
            m?.document?.statut?.libelle,
            m?.document?.statut?.label,
            typeof m?.document?.statut === 'string' ? m.document.statut : null,
            m?.document_statut_obj?.titre,
            m?.document?.statut_relation?.titre,
            m?.statut_document_obj?.titre
        ];
        for (const raw of candidates) {
            if (raw === undefined || raw === null || raw === '') {
                continue;
            }
            if (typeof raw === 'object') {
                const s = String(raw.titre ?? raw.nom ?? raw.libelle ?? raw.label ?? '').trim();
                if (s) {
                    return s;
                }
            } else {
                const s = String(raw).trim();
                if (s) {
                    return s;
                }
            }
        }
        return null;
    }

    /** Badge : statuts document (couleurs) + repli types de mouvement entrée/sortie. */
    private badgeClassEtatColumn(label: string): string {
        const e = label.toLowerCase();
        if (
            e.includes('payé') ||
            e.includes('paye') ||
            e.includes('validé') ||
            e.includes('valide') ||
            e.includes('approuvé') ||
            e.includes('approuve') ||
            e.includes('terminé') ||
            e.includes('termine') ||
            e.includes('clôtur') ||
            e.includes('clotur') ||
            e.includes('livré') ||
            e.includes('livre') ||
            e.includes('facturé') ||
            e.includes('facture')
        ) {
            return 'ms-badge ms-badge--success';
        }
        if (
            e.includes('annul') ||
            e.includes('refus') ||
            e.includes('rejet') ||
            e.includes('expir')
        ) {
            return 'ms-badge ms-badge--warn';
        }
        if (
            e.includes('brouillon') ||
            e.includes('non payé') ||
            e.includes('non paye') ||
            e.includes('en attente') ||
            e.includes('créé') ||
            e.includes('cree')
        ) {
            return 'ms-badge ms-badge--neutral';
        }
        return this.badgeClassMovementType(label);
    }

    /** Types de mouvement stock (entrée / sortie) — repli si pas de statut document. */
    private badgeClassMovementType(etat: string): string {
        const e = etat.toLowerCase();
        if (e.includes('sortie') || e.includes('out') || e.includes('perte')) {
            return 'ms-badge ms-badge--warn';
        }
        if (
            e.includes('entr') ||
            e.includes('reçu') ||
            e.includes('recu') ||
            e.includes('in') ||
            e.includes('réception')
        ) {
            return 'ms-badge ms-badge--success';
        }
        return 'ms-badge ms-badge--neutral';
    }

    onSearchInput(value: string): void {
        this.searchTerm = value;
        this.resetPagination();
        setTimeout(() => feather.replace());
    }

    toggleFilter(): void {
        this.showFilter = !this.showFilter;
        setTimeout(() => feather.replace());
    }

    /** Tableau `informations_logistique` du produit (plusieurs formes de réponse API). */
    private getInformationsLogistiqueArray(): any[] {
        const p = this.productDetail;
        if (!p) {
            return [];
        }
        const raw =
            p.informations_logistique ??
            p.informationsLogistique ??
            p.product?.informations_logistique ??
            p.data?.informations_logistique;
        return Array.isArray(raw) ? raw : [];
    }

    private logisticLineEntrepotId(line: any): number | null {
        const raw =
            line?.entrepot_id ??
            line?.entrepotId ??
            line?.entrepot?.id ??
            line?.warehouse_id ??
            line?.warehouse?.id;
        if (raw == null || raw === '') {
            return null;
        }
        const n = Number(raw);
        return isNaN(n) ? null : n;
    }

    private lineEntrepotDisplayName(line: any): string {
        return String(
            line?.entrepot?.nom ??
                line?.entrepot_nom ??
                line?.nom_entrepot ??
                line?.warehouse?.name ??
                ''
        ).trim();
    }

    /** true = « Tous les entrepôts » ou liste dégagée (clear) : pas de filtre entrepôt, min/max = sommes. */
    private isAllEntrepotsSelection(): boolean {
        return (
            this.selectedEntrepotId == null || this.selectedEntrepotId === this.ALL_ENTREPOTS_SELECT_ID
        );
    }

    /** Id entrepôt réel pour filtrer les mouvements, ou null si tous. */
    private getSelectedWarehouseIdForFilter(): number | null {
        if (this.isAllEntrepotsSelection()) {
            return null;
        }
        return this.selectedEntrepotId;
    }

    /**
     * Ligne `informations_logistique` pour l’entrepôt choisi (un seul entrepôt) ;
     * si « Tous les entrepôts », ne sert pas au min/max (voir getSumLogisticThreshold).
     */
    private getLogisticLineForSelection(): any | null {
        const infos = this.getInformationsLogistiqueArray();
        if (!infos.length) {
            return null;
        }
        if (this.isAllEntrepotsSelection()) {
            return null;
        }
        if (this.selectedEntrepotId != null) {
            const sel = Number(this.selectedEntrepotId);
            if (!isNaN(sel)) {
                const byId = infos.find((l: any) => this.logisticLineEntrepotId(l) === sel);
                if (byId) {
                    return byId;
                }
            }
            const selNom = this.entrepotsList.find((e) => e.id === this.selectedEntrepotId)?.nom;
            if (selNom) {
                const target = this.normalizeWarehouseLabel(selNom);
                const byName = infos.find((l: any) => {
                    const n = this.normalizeWarehouseLabel(this.lineEntrepotDisplayName(l));
                    return n !== '' && n === target;
                });
                if (byName) {
                    return byName;
                }
            }
            return null;
        }
        return infos[0] ?? null;
    }

    /** Seuils min / max issus uniquement du produit (champs API possibles). */
    private formatLogisticThreshold(line: any, kind: 'min' | 'max'): string | null {
        if (line == null) {
            return null;
        }
        const minKeys = ['minimum', 'min', 'quantite_min', 'stock_min', 'qty_min', 'seuil_min'];
        const maxKeys = ['maximum', 'max', 'quantite_max', 'stock_max', 'qty_max', 'seuil_max'];
        const keys = kind === 'min' ? minKeys : maxKeys;
        for (const k of keys) {
            const v = line[k];
            if (v != null && v !== '') {
                return String(v);
            }
        }
        return null;
    }

    private getProductMinMaxLabel(kind: 'min' | 'max'): string {
        if (this.isAllEntrepotsSelection()) {
            return this.getSumLogisticThreshold(kind);
        }
        const line = this.getLogisticLineForSelection();
        return this.formatLogisticThreshold(line, kind) ?? '—';
    }

    /** Somme des minimum ou maximum sur toutes les lignes `informations_logistique`. */
    private getSumLogisticThreshold(kind: 'min' | 'max'): string {
        const infos = this.getInformationsLogistiqueArray();
        if (!infos.length) {
            return '—';
        }
        let sum = 0;
        let hasAny = false;
        for (const line of infos) {
            const s = this.formatLogisticThreshold(line, kind);
            if (s != null && s !== '') {
                const n = parseFloat(String(s).replace(',', '.'));
                if (!isNaN(n)) {
                    sum += n;
                    hasAny = true;
                }
            }
        }
        if (!hasAny) {
            return '—';
        }
        const rounded = Math.round(sum * 1000) / 1000;
        return Number.isInteger(rounded) ? String(rounded) : String(rounded);
    }

    get etatStockLabel(): string {
        if (this.stockAllEntrepots != null) {
            const wid = this.getSelectedWarehouseIdForFilter();
            if (wid != null) {
                const q = this.stockAllEntrepots.byEntrepotId.get(wid);
                if (q !== undefined) {
                    return `${q} en stock`;
                }
                return '—';
            }
            return `${this.stockAllEntrepots.total} en stock`;
        }
        const m = this.stockMeta;
        if (!m) return '—';
        const q =
            m?.quantite_totale ??
            m?.total_quantity ??
            m?.stock_total ??
            m?.quantity_total;
        if (q != null && q !== '') return `${q} en stock`;
        return '—';
    }

    /** Détail par entrepôt (infobulle) quand l’API renvoie stocks_by_entrepot */
    get etatStockTitle(): string {
        const wid = this.getSelectedWarehouseIdForFilter();
        if (wid != null) {
            const name = this.entrepotsList.find((e) => e.id === wid)?.nom ?? '';
            return name ? `Stock pour : ${name}` : '';
        }
        const lines = this.stockAllEntrepots?.detailLines;
        if (!lines?.length) {
            return '';
        }
        return lines.join(' · ');
    }

    get stockMinLabel(): string {
        return this.getProductMinMaxLabel('min');
    }

    get stockMaxLabel(): string {
        return this.getProductMinMaxLabel('max');
    }

    /** Infobulle min/max lorsque « Tous les entrepôts » : explique la somme. */
    get minMaxThresholdsTitle(): string {
        return this.isAllEntrepotsSelection()
            ? 'Somme des seuils min. / max. sur toutes les lignes logistiques du produit'
            : '';
    }
}
